import Close from '@mdi/svg/svg/close.svg?react';
import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { EMPTY_OBJ } from '../../constants';
import { usePrevious } from '../../hooks/usePrevious';
import { Scrollbars } from '../../mixins';
import { CLICK_DELAY, COLOR, FONT_SIZE, SIZE, Theme } from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { MDIIcon } from '../MDIIcon';
import { type PanelProps } from './Panel.types';

export * from './Panel.types';

// eslint-disable-next-line
// @ts-ignore
let setDragTimeout;

export const Panel: React.FC<PanelProps> = ({
  canConfirm = false,
  onConfirm,
  closeOnOutsideClick = false,
  actions,
  bodyClassName = '',
  bodyStyle = EMPTY_OBJ,
  children = <div />,
  className = '',
  colorMode = 'light',
  dataTestId = 'panel',
  fixedFooter = false,
  fixedHeader = false,
  header,
  icon,
  isBanner,
  isFullHeight = false,
  isOpen = false,
  onClose,
  position: positionProp = 'left',
  showCloseButton = true,
  style = {},
  subtitle,
  title,
  type,
}) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const prevIsOpen = usePrevious(isOpen);
  const [position, setPosition] = useState<any>({});
  const [previouslyFocused, setPreviouslyFocused] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const { left, right, top } = position;

  const saveFocusedElement = useCallback(() => {
    const focused = document.activeElement;

    if (!focused) {
      return;
    }

    const focusedElements = previouslyFocused;
    // Save the focused element
    // eslint-disable-next-line
    // @ts-ignore
    focusedElements.push(focused);

    // Save the parent, as a backup
    if (focused.parentElement) {
      // eslint-disable-next-line
      // @ts-ignore
      focusedElements.push(focused.parentElement);
    }

    // Save the first <a> or <button> of a parent .action element,
    // as a second backup.
    if (focused.closest('.action')) {
      // eslint-disable-next-line
      // @ts-ignore
      focusedElements.push(focused.closest('.action').querySelector('a, button'));
    }
    setPreviouslyFocused(focusedElements);
  }, [previouslyFocused]);

  const focusOnPrevious = useCallback(() => {
    for (const element of previouslyFocused) {
      if (element) {
        // eslint-disable-next-line
        // @ts-ignore
        element.focus();
        break;
      }
    }
    setPreviouslyFocused([]);
  }, [previouslyFocused]);

  const keyDown = useCallback(
    // eslint-disable-next-line
    // @ts-ignore
    (event) => {
      if (!event.skipModal) {
        if (event.keyCode === 27) {
          // eslint-disable-next-line
          // @ts-ignore
          onClose();
        } else if (event.keyCode === 13) {
          if (canConfirm) {
            // eslint-disable-next-line
            // @ts-ignore
            onConfirm?.();
          } else {
            event.preventDefault();
            event.stopPropagation();
          }
        }
      }
    },
    [canConfirm, onClose, onConfirm],
  );

  const setListener = useCallback(() => document.addEventListener('keydown', keyDown), [keyDown]);

  const init = useCallback(() => {
    // Get the position of the panel, so we can fix it
    // eslint-disable-next-line
    // @ts-ignore
    const { left, top } = panelRef.current.getBoundingClientRect();

    if (showCloseButton) {
      // eslint-disable-next-line
      // @ts-ignore
      closeRef.current.focus();
    }
    setListener();

    // Save the current focused element, for when we close this
    saveFocusedElement();

    return {
      top,
      ...(positionProp === 'right' ? { right: 0 } : { left }),
    };
  }, [positionProp, saveFocusedElement, setListener, showCloseButton]);

  const removeListener = useCallback(() => {
    document.removeEventListener('keydown', keyDown);
    focusOnPrevious();
  }, [focusOnPrevious, keyDown]);

  const initDragging = () => {
    // eslint-disable-next-line
    // @ts-ignore
    clearTimeout(setDragTimeout);
    setDragTimeout = setTimeout(() => {
      setIsDragging(true);
    }, CLICK_DELAY);
  };

  const endDragging = () => {
    // eslint-disable-next-line
    // @ts-ignore
    clearTimeout(setDragTimeout);
    if (isDragging) {
      setDragTimeout = setTimeout(() => {
        setIsDragging(false);
      }, CLICK_DELAY);
    }
  };

  const handleOutsideClick = () => {
    if (!isDragging && closeOnOutsideClick) {
      // eslint-disable-next-line
      // @ts-ignore
      onClose();
    }
  };

  if (!prevIsOpen && isOpen && panelRef.current) {
    const newPosition = init();
    setPosition(newPosition);
  } else if (prevIsOpen && !isOpen) {
    removeListener();
  }

  const wrapperClass = classNames(
    'panel-wrapper',
    {
      'fixed-footer': fixedFooter && !!actions,
      'fixed-header': fixedHeader,
      footerless: !actions,
      open: isOpen,
      'with-subtitle': !!subtitle,
    },
    type,
    `position-${positionProp}`,
    colorMode,
    className,
  );

  const {
    component: { desktopTopNavHeight, bannerHeight },
  } = Theme;

  const wrapperStyle: any = {
    ...(left || right || top ? { position: 'fixed' } : {}),
    ...(left ? { left } : {}),
    ...(right ? { right } : {}),
    ...(top ? { top } : {}),
  };

  // eslint-disable-next-line
  // @ts-ignore
  const offsetHeight = isFullHeight ? 0 : desktopTopNavHeight + (!!isBanner && bannerHeight);

  return (
    <SectionWrapper
      className={wrapperClass}
      onClick={handleOutsideClick}
      onMouseDown={initDragging}
      onMouseUp={endDragging}
      offsetHeight={offsetHeight}
      ref={panelRef}
      style={wrapperStyle}
    >
      <AsidePanel
        className="panel"
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        style={style}
        data-testid={dataTestId}
      >
        <header>
          {header}
          {!header && (icon || title) && (
            <Flex alignItems="center" className="header-wrapper">
              {!!icon && icon}
              <div className="title-wrapper">
                {title && <h1 className="title">{title}</h1>}
                {subtitle && <h2 className="subtitle">{subtitle}</h2>}
              </div>
            </Flex>
          )}
          {showCloseButton && (
            <button
              className="close"
              data-testid={`${dataTestId}-close`}
              title="Close"
              onClick={onClose}
              onKeyDown={a11yKey(onClose)}
              ref={closeRef}
              tabIndex={0}
            >
              <MDIIcon icon={Close} />
            </button>
          )}
        </header>
        <section className={`body ${bodyClassName}`} style={bodyStyle}>
          {isOpen && children}
        </section>
        {actions && (
          <footer>
            {actions.map((action, index) => (
              <Button
                badge={action.badge}
                colorMode={action.colorMode || colorMode}
                dataTestId={`${dataTestId}-${action.text}`}
                key={`panel-button-${index}`}
                disabled={action.disabled}
                icon={action.icon}
                onClick={action.onClick}
                isPrimary={action.isPrimary}
                text={action.text}
                tooltip={action.tooltip || null}
              />
            ))}
          </footer>
        )}
      </AsidePanel>
    </SectionWrapper>
  );
};

const headerRowHeight = '36px';
const headerRowHeightWithSubtitle = '52px';
const sectionPadding = '16px';
const panelHeaderFooterHeight = '68px';
const panelHeaderWithSubtitleHeight = '84px';
const basePanelWidth = '120px';
const transformDuration = '.25s';
const widthTransformDuration = '.35s';
const visibilityDuration = widthTransformDuration;
// Styled Components
const SectionWrapper = styled('section').withConfig({
  shouldForwardProp: (prop) => !['offsetHeight'].includes(prop),
})<{ offsetHeight: number }>`
  background: rgba(0, 0, 0, 0.1);
  bottom: 0;
  display: grid;
  font-family: inherit;
  opacity: 0;
  visibility: hidden;
  overflow: hidden;
  position: absolute;
  top: 0;
  transition:
    opacity ${transformDuration} ease-out,
    width 0s ${widthTransformDuration},
    visibility ${visibilityDuration};
  width: 0;
  z-index: 1000;
  height: ${(p) => `calc(100vh - ${p.offsetHeight}px)`};

  &.with-subtitle {
    &.fixed-header {
      .body {
        top: ${panelHeaderWithSubtitleHeight};
        height: calc(100% - ${panelHeaderWithSubtitleHeight});
      }
    }

    aside.panel {
      > header {
        height: ${panelHeaderWithSubtitleHeight};

        button.close {
          height: ${headerRowHeightWithSubtitle};
          line-height: ${headerRowHeightWithSubtitle};
          width: ${headerRowHeightWithSubtitle};
        }
      }
    }
  }

  &.dark {
    background: rgba(0, 0, 0, 0.5);
    color: ${COLOR['white']};

    aside.panel {
      background: ${COLOR['dark-gray-darker']};

      & > header {
        .title {
          color: ${COLOR['white']};
        }
        .icon {
          fill: ${COLOR['white']};
        }
      }
    }
  }

  &.footerless .panel {
    padding-bottom: ${sectionPadding};
  }

  &.position-left {
    grid-template-areas: 'panel .';
    grid-template-columns: calc(${basePanelWidth} * 3) 1fr;
    grid-template-rows: 1fr;
    left: 0;

    &.slim {
      grid-template-columns: calc(${basePanelWidth} * 3) 1fr;
      grid-template-rows: 1fr;
    }

    &.wide {
      grid-template-columns: calc(${basePanelWidth} * 4) 1fr;
      grid-template-rows: 1fr;
    }

    &.ultra-wide {
      grid-template-columns: calc(${basePanelWidth} * 6) 1fr;
      grid-template-rows: 1fr;
    }

    aside.panel {
      transform: translateX(-100%);
    }
  }

  &.position-right {
    grid-template-areas: '. panel';
    grid-template-columns: 1fr calc(${basePanelWidth} * 3);
    grid-template-rows: 1fr;
    right: 0;

    &.slim {
      grid-template-columns: 1fr calc(${basePanelWidth} * 3);
      grid-template-rows: 1fr;
    }

    &.wide {
      grid-template-columns: 1fr calc(${basePanelWidth} * 4);
      grid-template-rows: 1fr;
    }

    &.ultra-wide {
      grid-template-columns: 1fr calc(${basePanelWidth} * 6);
      grid-template-rows: 1fr;
    }
  }

  &.open {
    opacity: 1;
    visibility: visible;
    transition:
      opacity ${transformDuration} ease-in,
      visibility ${visibilityDuration};
    width: 100%;

    aside.panel {
      transition: transform ${transformDuration} ease-in;
      transform: translateX(0);
    }
  }

  &.fixed-header {
    .panel > header {
      position: fixed;
      top: 0;
      z-index: 10;
    }

    .body {
      top: ${panelHeaderFooterHeight};
    }
  }

  &.fixed-footer {
    footer {
      position: fixed;
      bottom: 0;
    }

    .body {
      bottom: ${panelHeaderFooterHeight};
    }
  }

  &.fixed-header,
  &.fixed-footer {
    aside.panel {
      overflow: hidden;
    }

    .body {
      ${Scrollbars({
        width: '5px',
        height: '8px',
        border: '0.5px',
        foregroundColor: 'rgba(150, 150, 150, 0.5)',
        backgroundColor: 'transparent',
      })};
      height: calc(100% - ${panelHeaderFooterHeight});
      overflow-y: auto;
      position: relative;
    }
  }

  &.fixed-header.fixed-footer {
    .body {
      height: calc(100% - ${panelHeaderFooterHeight} * 2);
    }
  }
`;

const AsidePanel = styled.aside`
  ${Scrollbars({
    width: '5px',
    height: '8px',
    border: '0.5px',
    foregroundColor: 'rgba(150, 150, 150, 0.5)',
    backgroundColor: 'transparent',
  })};
  background: ${COLOR['light-gray-lighter']};
  border-style: none;
  box-shadow:
    rgba(0, 0, 0, 0.25) 0px 14px 45px,
    rgba(0, 0, 0, 0.22) 0px 10px 18px;
  color: inherit;
  grid-area: panel;
  height: 100%;
  margin: 0;
  overflow: auto;
  padding: 0;
  position: relative;
  transition: transform ${transformDuration} ease-out;
  width: 100%;

  &:focus {
    outline: none;
  }

  &,
  > header,
  > section.body,
  > footer {
    display: block;
  }

  > header {
    height: ${panelHeaderFooterHeight};
    padding: ${sectionPadding};
    width: 100%;

    .header-wrapper {
      width: calc(100% - 60px);
    }

    .title-wrapper {
      width: 100%;
    }

    .title,
    .subtitle {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 0;
      max-width: calc(100% - ${headerRowHeight} + ${sectionPadding});
    }

    .title {
      color: ${COLOR['dark-gray-dark']};
      font-size: ${FONT_SIZE['20']};
      font-weight: 300;
      line-height: ${headerRowHeight};
    }

    .subtitle {
      color: ${COLOR['dark-gray-dark']};
      font-size: ${FONT_SIZE['14']};
      font-weight: 400;
      margin: 0;
    }

    .icon {
      fill: ${COLOR['dark-gray-dark']};
      margin-right: 7px;
    }

    button.close {
      background: none;
      border: none;
      box-sizing: content-box;
      color: ${COLOR['textColor']};
      cursor: pointer;
      font-size: calc(${headerRowHeight} - 8px);
      height: ${headerRowHeight};
      line-height: ${headerRowHeight};
      padding: ${sectionPadding};
      position: absolute;
      right: 0;
      top: 0;
      width: ${headerRowHeight};

      &:hover,
      &:focus {
        background: ${COLOR['primary']};
        color: ${COLOR['white']};
        outline: none;

        .icon {
          fill: ${COLOR['white']};
        }
      }
    }
  }

  > section.body {
    padding: 0 ${sectionPadding};

    &,
    p,
    li {
      font-size: ${FONT_SIZE['14']};
      font-weight: 300;
    }

    p {
      margin-bottom: ${SIZE['20']};
    }

    .accordion-container .accordion-item header {
      margin-left: ${FONT_SIZE['16']};
      margin-right: ${FONT_SIZE['16']};
    }
  }

  > footer {
    width: 100%;
    height: ${panelHeaderFooterHeight};
    padding: ${sectionPadding};
    text-align: right;

    button,
    .button {
      display: inline-block;
      margin-left: 10px;
    }
  }
`;

// Display Name
Panel.displayName = 'Panel';

export default Panel;
